import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AuditLogValueDisplay } from '../../../components/admin/AuditLogValueDisplay';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetAuditLogByIdQuery } from '../../../store/api/auditLogsApi';
import { formatDate, formatRelativeTime } from '../../../utils/dateFormatter';
import { getTranslatedAction, getTranslatedTarget } from '../../../utils/auditLogTranslations';

export default function AuditLogDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const logId = id || '';

  const { data, isLoading, error } = useGetAuditLogByIdQuery(logId, {
    skip: !logId,
  });

  const log = data?.data?.log;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !log) {
    return (
      <div className="text-center py-12">
        <p className="text-danger mb-4">
          {t('admin.auditLogs.details.error.title') || 'Error loading audit log'}
        </p>
        <p className="text-neutral-medium mb-6">
          {t('admin.auditLogs.details.error.notFound') || 'Audit log not found'}
        </p>
        <Link
          to="/admin/audit-logs"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200"
        >
          {t('admin.auditLogs.details.back') || 'Back to Audit Logs'}
        </Link>
      </div>
    );
  }

  const getResultColor = (result: string) => {
    return result === 'success' || result.startsWith('success')
      ? 'bg-green-500/20 text-green-400 border-green-500/50'
      : 'bg-red-500/20 text-red-400 border-red-500/50';
  };

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <Link
          to="/admin/audit-logs"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline text-sm sm:text-base"
        >
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('admin.auditLogs.details.back') || 'Back to Audit Logs'}
        </Link>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.auditLogs.details.title') || 'Audit Log Details'}
        </h1>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.auditLogs.details.info.logId') || 'Log ID'}: {log._id.slice(-8)}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Info Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Log Information */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.1s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.auditLogs.details.info.title') || 'Log Information'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.info.action') || 'Action'}
                </label>
                <p className="text-neutral-light font-semibold">
                  {getTranslatedAction(log.action, t)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.info.target') || 'Target'}
                </label>
                <p className="text-neutral-light font-semibold">
                  {getTranslatedTarget(log.target, t)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.info.actor') || 'Actor'}
                </label>
                <p className="text-neutral-light">
                  {log.actorId === 'system' ? (
                    <span className="text-brand-accent font-semibold">System</span>
                  ) : (
                    <Link
                      to={`/admin/users/${log.actorId}`}
                      className="text-brand-accent hover:text-brand-accent/80 transition-colors duration-200"
                    >
                      {log.actorId}
                    </Link>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.info.result') || 'Result'}
                </label>
                <span
                  className={`inline-block px-3 py-1 rounded text-sm font-semibold border ${getResultColor(log.result)}`}
                >
                  {log.result === 'success' || log.result.startsWith('success')
                    ? t('admin.auditLogs.filters.success') || 'نجح'
                    : log.result.startsWith('failed:')
                      ? log.result.replace('failed: ', '')
                      : t('admin.auditLogs.filters.failed') || 'فشل'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.info.createdAt') || 'Created At'}
                </label>
                <p className="text-neutral-light">
                  {formatDate(log.createdAt, 'PPpp', currentLanguage)}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(log.createdAt, currentLanguage)}
                </p>
              </div>
              {log.updatedAt && log.updatedAt !== log.createdAt && (
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.auditLogs.details.info.updatedAt') || 'Updated At'}
                  </label>
                  <p className="text-neutral-light">
                    {formatDate(log.updatedAt, 'PPpp', currentLanguage)}
                  </p>
                  <p className="text-xs text-neutral-medium mt-1">
                    {formatRelativeTime(log.updatedAt, currentLanguage)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Values */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.auditLogs.details.values.title') || 'Values'}
            </h2>
            <AuditLogValueDisplay log={log} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.auditLogs.details.quickInfo.title') || 'Quick Info'}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.quickInfo.logId') || 'Log ID'}
                </label>
                <p className="text-neutral-light font-mono text-xs break-all">{log._id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.quickInfo.hasOldValue') || 'Has Old Value'}
                </label>
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    log.oldValue
                      ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                      : 'bg-neutral-medium/20 text-neutral-medium border border-neutral-medium/50'
                  }`}
                >
                  {log.oldValue ? t('common.yes') || 'نعم' : t('common.no') || 'لا'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.auditLogs.details.quickInfo.hasNewValue') || 'Has New Value'}
                </label>
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    log.newValue
                      ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                      : 'bg-neutral-medium/20 text-neutral-medium border border-neutral-medium/50'
                  }`}
                >
                  {log.newValue ? t('common.yes') || 'نعم' : t('common.no') || 'لا'}
                </span>
              </div>
            </div>
          </div>

          {/* Related Actions */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.3s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.auditLogs.details.actions.title') || 'Related Actions'}
            </h2>
            <div className="space-y-2">
              {log.actorId !== 'system' && typeof log.actorId === 'number' && (
                <Link
                  to={`/admin/users/${log.actorId}`}
                  className="block w-full px-4 py-2 text-center bg-blue-500/20 text-blue-400 border border-blue-500/50 font-heading font-semibold rounded-lg hover:bg-blue-500/30 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {t('admin.auditLogs.details.actions.viewUser') || 'View User'}
                </Link>
              )}
              {log.target === 'order' && log.newValue && typeof (log.newValue as Record<string, unknown>).orderId === 'string' && (
                <Link
                  to={`/admin/orders/${(log.newValue as Record<string, unknown>).orderId as string}`}
                  className="block w-full px-4 py-2 text-center bg-purple-500/20 text-purple-400 border border-purple-500/50 font-heading font-semibold rounded-lg hover:bg-purple-500/30 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {t('admin.auditLogs.details.actions.viewOrder') || 'View Order'}
                </Link>
              )}
              <Link
                to={`/admin/audit-logs${log.actorId !== 'system' ? `/${log.actorId}` : ''}?action=${log.action}&target=${log.target}`}
                className="block w-full px-4 py-2 text-center bg-brand-accent/20 text-brand-accent border border-brand-accent/50 font-heading font-semibold rounded-lg hover:bg-brand-accent/30 transition-all duration-200 hover:scale-105 active:scale-95"
              >
                {t('admin.auditLogs.details.actions.viewSimilar') || 'View Similar Logs'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

